import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useLang } from '../LangContext';
import { canAccess, normalizeAdmin } from '../adminAccess';
import {
  BanknotesIcon, BuildingOffice2Icon, ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, CubeIcon, HomeIcon,
  ExclamationTriangleIcon, LifebuoyIcon,
  MapPinIcon, ShieldCheckIcon, SparklesIcon, TagIcon, UserGroupIcon, UserPlusIcon,
} from '@heroicons/react/24/outline';

export default function Layout({ user, onLogout }) {
  const { lang, setLang, tr, languages } = useLang();
  const admin = normalizeAdmin(user);
  const [openSupport, setOpenSupport] = useState(0);
  const [pendingUsers, setPendingUsers] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // Close the drawer whenever the route changes (mobile).
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!canAccess(admin, 'workReviews')) return undefined;
    const unsub = onSnapshot(
      query(collection(db, 'orders'), where('status', '==', 'pending_admin_review')),
      snap => setPendingReviews(snap.size),
      () => setPendingReviews(0),
    );
    return () => unsub();
  }, [admin]);

  useEffect(() => {
    if (!canAccess(admin, 'support')) return undefined;
    const unsub = onSnapshot(collection(db, 'support_tickets'), snap => {
      const count = snap.docs
        .map(d => d.data())
        .filter(t => (t.status || 'open') !== 'closed').length;
      setOpenSupport(count);
    }, () => setOpenSupport(0));
    return () => unsub();
  }, [admin]);

  useEffect(() => {
    if (!canAccess(admin, 'users')) return undefined;
    let alive = true;
    const refresh = async () => {
      try {
        const result = await httpsCallable(functions, 'listNewUsers')();
        if (alive) setPendingUsers(result.data?.users?.length || 0);
      } catch (_) {
        if (alive) setPendingUsers(0);
      }
    };
    refresh();
    window.addEventListener('pakmazar:pending-users-changed', refresh);
    const timer = window.setInterval(refresh, 30000);
    return () => {
      alive = false;
      window.removeEventListener('pakmazar:pending-users-changed', refresh);
      window.clearInterval(timer);
    };
  }, [admin, location.pathname]);

  const nav = [
    { to: '/', icon: HomeIcon, key: 'dashboard' },
    { to: '/orders', icon: ClipboardDocumentListIcon, key: 'orders' },
    { to: '/work-reviews', icon: ClipboardDocumentCheckIcon, key: 'workReviews', badge: pendingReviews },
    { to: '/complaints', icon: ExclamationTriangleIcon, key: 'complaints', accessKey: 'orders' },
    { to: '/workers', icon: BuildingOffice2Icon, key: 'workers' },
    { to: '/clients', icon: UserGroupIcon, key: 'clients', accessKey: 'users' },
    { to: '/cemeteries', icon: MapPinIcon, key: 'cemeteries' },
    { to: '/packages', icon: CubeIcon, key: 'packages' },
    { to: '/flowers', icon: SparklesIcon, key: 'flowers' },
    { to: '/promo-codes', icon: TagIcon, key: 'promoCodes' },
    { to: '/users', icon: UserPlusIcon, key: 'users', labelKey: 'newUsers', badge: pendingUsers },
    { to: '/communications', icon: ChatBubbleLeftRightIcon, key: 'communications' },
    { to: '/support', icon: LifebuoyIcon, key: 'support', badge: openSupport },
    { to: '/finance', icon: BanknotesIcon, key: 'finance' },
    { to: '/admin-access', icon: ShieldCheckIcon, key: 'adminAccess' },
  ].filter(item => canAccess(admin, item.accessKey || item.key));

  return (
    <div className="flex min-h-screen bg-[#FAF7F0]">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-16 bg-white/95 border-b border-[#E9E0E9] flex items-center gap-3 px-4 shadow-sm backdrop-blur">
        <button
          onClick={() => setNavOpen(true)}
          aria-label="Open menu"
          className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-xl"
        >
          ☰
        </button>
        <img src="/pakmazar-mark.png" alt="" className="h-10 w-10 rounded-xl object-cover" />
        <div><p className="font-extrabold text-[#251A28]">PakMazar</p><p className="text-[10px] font-semibold uppercase tracking-[.16em] text-[#817886]">{tr('adminPanel')}</p></div>
      </header>

      {/* Backdrop */}
      {navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/40"
          aria-hidden="true"
        />
      )}

      <aside
        className={`w-[272px] bg-white border-r border-[#E9E0E9] flex flex-col shadow-[8px_0_30px_rgba(53,16,68,.04)]
          fixed inset-y-0 left-0 z-50 transform transition-transform duration-200
          ${navOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:z-auto`}
      >
        <div className="p-5 border-b border-[#E9E0E9]">
          <div className="flex items-center gap-3">
            <img src="/pakmazar-mark.png" alt="PakMazar logo" className="h-12 w-12 rounded-2xl object-cover shadow-md" />
            <div className="flex-1">
              <p className="font-extrabold text-[#251A28]">PakMazar</p>
              <p className="text-[10px] font-bold uppercase tracking-[.17em] text-[#817886]">{tr('adminPanel')}</p>
            </div>
            <button
              onClick={() => setNavOpen(false)}
              aria-label="Close menu"
              className="lg:hidden w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400"
            >
              ✕
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {nav.map(({ to, icon: Icon, key, labelKey, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="flex-1">{tr(labelKey || key)}</span>
              {badge > 0 && (
                <span className="min-w-[22px] rounded-full bg-[#D9B447] px-1.5 py-0.5 text-center text-[10px] font-extrabold text-[#351044]">{badge}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100 space-y-2">
          {/* Three languages, each directly selectable: cycling through them
              made Russian two clicks away and gave no sense of what was
              available. The choice persists in localStorage. */}
          <div className="px-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-2 mb-1">
              🌐 {tr('language')}
            </p>
            <div className="flex gap-1">
              {languages.map(option => (
                <button
                  key={option.code}
                  onClick={() => setLang(option.code)}
                  aria-pressed={lang === option.code}
                  title={option.label}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold transition ${
                    lang === option.code
                      ? 'bg-primary text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {option.short}
                </button>
              ))}
            </div>
          </div>

          {admin && (
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-white text-xs font-bold">
                {admin.name?.[0] ?? 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-700 truncate">{admin.name}</p>
                <p className="text-xs text-gray-400 truncate">{admin.isSuperAdmin ? 'Super admin' : `${admin.cityScope || 'all'} manager`}</p>
              </div>
            </div>
          )}
          <button onClick={onLogout} className="sidebar-link w-full text-red-500 hover:bg-red-50">
            <span>🚪</span><span>{tr('logout')}</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden overflow-y-auto pt-16 lg:pt-0 w-full min-w-0">
        <Outlet context={{ user: admin }} />
      </main>
    </div>
  );
}
